import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Checklist, Drawer, Input, Field } from '@itds/elements/index.js';
import FormUtils from '@itds/utils/Form';
import { PASSWORD_RULES } from '@itds/Constants';
import { Colors } from '@itds/Theme';

import Api from '@carrier/utils/Api';

const Edit = ({ admin, onClose, onSave, show }) => {
  const styles = {
    password_rule: {
      marginBottom: 10
    },
    password_rule_valid: {
      color: '#999'
    },
    icon: {
      fontSize: 20,
      verticalAlign: 'middle',
      color: '#ccc',
      marginRight: 2
    },
    icon_green: {
      fontSize: 20,
      verticalAlign: 'middle',
      color: Colors.GREEN.hex,
      marginRight: 2
    }
  };
  const [data, setData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    permissions: []
  });

  const [errors, setErrors] = useState([]);
  const [password_errors, setPasswordErrors] = useState([]);
  const [save_error, setSaveError] = useState(false);
  const permissions = ['users', 'carrier', 'forms', 'products', 'vendors'];

  useEffect(() => {
    const new_admin = admin || {};

    setData({
      id: new_admin.id,
      carrier_id: new_admin.carrier_id,
      first_name: new_admin.first_name || '',
      last_name: new_admin.last_name || '',
      email: new_admin.email || '',
      password: '',
      permissions: new_admin.permissions
    });
  }, [admin]);

  const handleInputChange = (name, e) => {
    setErrors([]);
    setData({ ...data, [name]: e.target.value });

    if (name === 'password') {
      const password = e.target.value;
      const new_errors = [];

      PASSWORD_RULES.forEach(rule => {
        if (!rule.pattern.test(password)) {
          new_errors.push(rule.name);
        }
      });

      setPasswordErrors(new_errors);
    }
  };

  const handleChecklistChange = (name, selected) => {
    setData({ ...data, [name]: selected });
  };

  const handleSave = () => {
    const { id, ...payload } = data;

    const errors = [];

    if (!data.first_name) {
      errors.push('first_name');
    }

    if (!data.last_name) {
      errors.push('last_name');
    }

    if (!FormUtils._validateEmail(data.email)) {
      errors.push('email');
    }

    if (!admin.id && !data.password) {
      errors.push('password');
    }

    setErrors(errors);

    if (!errors.length) {
      Api.saveCarrierAdmin(id, payload)
        .then(() => {
          onSave();
          setSaveError(false);
        })
        .catch(() => {
          setSaveError(true);
          setTimeout(() => {
            setSaveError(false);
          }, 2000);
        });
    }
  };

  return (
    <Drawer
      alignRight={true}
      footer={
        <Button disabled={!!errors.length || !!password_errors.length} onClick={handleSave}>
          {data.id ? 'Save Changes' : 'Create New Admin'}
        </Button>
      }
      onClose={onClose}
      show={show}
      title={data.id ? 'Editing Admin' : 'New Admin'}
    >
      {save_error ? (
        <Alert type='error'>An error occurred while trying to save the admin.</Alert>
      ) : null}
      <div style={{ display: 'flex' }}>
        <Field label='First Name'>
          <Input
            invalid={errors.includes('first_name')}
            onChange={handleInputChange.bind(null, 'first_name')}
            required={true}
            value={data.first_name}
          />
        </Field>
        <Field label='Last Name' style={{ flex: 1, marginLeft: 10 }}>
          <Input
            invalid={errors.includes('last_name')}
            onChange={handleInputChange.bind(null, 'last_name')}
            required={true}
            value={data.last_name}
          />
        </Field>
      </div>

      <Field label='Email'>
        <Input
          invalid={errors.includes('email')}
          onChange={handleInputChange.bind(null, 'email')}
          required={true}
          value={data.email}
        />
      </Field>

      <Field label='Password'>
        <Input
          invalid={(data.password && !!password_errors.length) || errors.includes('password')}
          onChange={handleInputChange.bind(null, 'password')}
          type='password'
          value={data.password}
        />
      </Field>

      <Field label='Permissions'>
        <Checklist
          onChange={handleChecklistChange.bind(null, 'permissions')}
          options={permissions.map(o => ({ value: o }))}
          selected={data.permissions || []}
        />
      </Field>

      {data.password
        ? PASSWORD_RULES.map(rule => {
            const is_valid = !password_errors.includes(rule.name);

            return (
              <div
                key={rule.name}
                style={Object.assign(
                  {},
                  styles.password_rule,
                  is_valid && styles.password_rule_valid
                )}
              >
                {is_valid ? (
                  <i className='mdi mdi-checkbox-marked-circle-outline' style={styles.icon_green} />
                ) : (
                  <i className='mdi mdi-checkbox-blank-circle-outline' style={styles.icon} />
                )}{' '}
                {rule.label}
              </div>
            );
          })
        : null}
    </Drawer>
  );
};

Edit.propTypes = {
  admin: PropTypes.object,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  show: PropTypes.bool
};

export default Edit;
